import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { v2Api } from "@/lib/v2Api";
import {
  FileText,
  Search,
  Calendar,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Loader2,
  Filter,
  ArrowUpDown,
  Eye,
  MessageSquare,
  Wand2,
  Trash2,
  RefreshCw
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import RefreshButton from "@/components/v2/RefreshButton";
import TendersBoardView from "@/components/v2/TendersBoardView";
import { LayoutGrid, List as ListIcon } from "lucide-react";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RFPList = () => {
  const navigate = useNavigate();
  const { isDeptHead } = useAuth();
  const [rfps, setRfps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortField, setSortField] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");
  // Step 5 of the workflow rollout — List | Board toggle. Default
  // remains List for muscle-memory; admins flip to Board to see the
  // workspace's tenders grouped by bid.stage.
  const [viewMode, setViewMode] = useState(
    () => localStorage.getItem("tenders.viewMode") || "list",
  );

  useEffect(() => {
    fetchRFPs();
  }, []);

  const fetchRFPs = async () => {
    try {
      // Strangler swap — workspace-wide list now served by Postgres v2.
      // Field shape differs from the legacy /api/rfp/list (title vs name,
      // parse_status enum vs lowercase status, etc.) — see the
      // normalisation pass below.
      const data = await v2Api.rfp.list();
      const rows = (Array.isArray(data) ? data : []).map((r) => ({
        ...r,
        // Lowercase the parse_status so the existing getStatusConfig
        // and filter code keep working ("ANALYZED" → "analyzed").
        status: (r.parse_status || "").toLowerCase(),
        // Map the renamed fields so the existing JSX columns keep
        // rendering. New code should prefer the v2 names directly.
        name: r.title,
        total_pages: r.pages,
        file_size: r.file_size_bytes,
        // file_type from the basename extension
        file_type: (r.file_path || "").split(".").pop() || "",
        // analysis_summary.deadlines is the closest v2 analogue of
        // the v1 key_deadlines array; tolerate either shape.
        key_deadlines: (r.analysis_summary?.deadlines || r.analysis_summary?.key_deadlines || []),
      }));
      setRfps(rows);
    } catch (error) {
      console.error("Error fetching RFPs:", error);
      toast.error(error?.response?.data?.detail ?? "Failed to load RFPs");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (rfpId, rfpName) => {
    try {
      // Strangler swap — DELETE /api/v2/rfp/{id} also cleans the file
      // from disk best-effort (see backend rfp.delete_rfp).
      await v2Api.rfp.remove(rfpId);
      setRfps(rfps.filter(r => r.id !== rfpId));
      toast.success(`"${rfpName}" deleted successfully`);
    } catch (error) {
      toast.error(error?.response?.data?.detail ?? "Failed to delete RFP");
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "0 B";
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case "analyzed":
        return { icon: CheckCircle2, color: "bg-emerald-100 text-emerald-700", label: "Analyzed" };
      case "processing":
        return { icon: Clock, color: "bg-amber-100 text-amber-700", label: "Processing" };
      case "failed":
        return { icon: AlertTriangle, color: "bg-red-100 text-red-700", label: "Failed" };
      default:
        return { icon: FileText, color: "bg-blue-100 text-blue-700", label: status };
    }
  };

  // Filter and sort RFPs
  const filteredRFPs = rfps
    .filter(rfp => {
      const matchesSearch = rfp.name?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === "all" || rfp.status === statusFilter;
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      if (sortField === "file_size" || sortField === "total_pages") {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      } else if (sortField === "created_at") {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      }
      
      if (sortOrder === "asc") return aVal > bVal ? 1 : -1;
      return aVal < bVal ? 1 : -1;
    });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("desc");
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen" data-testid="rfp-list-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[#007AFF]" />
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="rfp-list-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-extrabold text-[#0F172A] font-['Manrope'] tracking-tight">
            RFP Documents
          </h1>
          <p className="text-[#64748B] mt-1">
            View and manage all processed RFP documents
          </p>
        </div>
        <div className="flex gap-3 items-center">
          {/* List | Board toggle (step 5). Persists in localStorage. */}
          <div className="inline-flex rounded-md border bg-white p-0.5">
            <button
              type="button"
              onClick={() => { setViewMode("list"); localStorage.setItem("tenders.viewMode", "list"); }}
              className={`px-2.5 py-1 text-xs rounded inline-flex items-center gap-1 ${
                viewMode === "list" ? "bg-slate-800 text-white" : "text-slate-600 hover:bg-slate-100"
              }`}
              title="List view"
            >
              <ListIcon className="w-3.5 h-3.5" /> List
            </button>
            <button
              type="button"
              onClick={() => { setViewMode("board"); localStorage.setItem("tenders.viewMode", "board"); }}
              className={`px-2.5 py-1 text-xs rounded inline-flex items-center gap-1 ${
                viewMode === "board" ? "bg-slate-800 text-white" : "text-slate-600 hover:bg-slate-100"
              }`}
              title="Board (Kanban) view"
            >
              <LayoutGrid className="w-3.5 h-3.5" /> Board
            </button>
          </div>
          <RefreshButton onClick={fetchRFPs} />
          {isDeptHead() && (
            <Button onClick={() => navigate("/upload")} className="bg-[#007AFF] hover:bg-[#0066DD]">
              <FileText className="w-4 h-4 mr-2" />
              Upload New RFP
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <Card className="border-[#E2E8F0]">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#64748B]">Total RFPs</p>
                <p className="text-2xl font-bold text-[#0F172A]">{rfps.length}</p>
              </div>
              <FileText className="w-8 h-8 text-[#007AFF] opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-[#E2E8F0]">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#64748B]">Analyzed</p>
                <p className="text-2xl font-bold text-emerald-600">
                  {rfps.filter(r => r.status === "analyzed").length}
                </p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-emerald-600 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-[#E2E8F0]">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#64748B]">Processing</p>
                <p className="text-2xl font-bold text-amber-600">
                  {rfps.filter(r => r.status === "processing").length}
                </p>
              </div>
              <Clock className="w-8 h-8 text-amber-600 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-[#E2E8F0]">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#64748B]">Total Pages</p>
                <p className="text-2xl font-bold text-[#0F172A]">
                  {rfps.reduce((sum, r) => sum + (r.total_pages || 0), 0)}
                </p>
              </div>
              <FileText className="w-8 h-8 text-[#64748B] opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-[#E2E8F0] mb-6">
        <CardContent className="pt-4 pb-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#64748B]" />
              <Input
                placeholder="Search by RFP name..."
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="search-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-[#64748B]" />
              <select
                className="px-3 py-2 border border-[#E2E8F0] rounded-md text-sm"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                data-testid="status-filter"
              >
                <option value="all">All Status</option>
                <option value="analyzed">Analyzed</option>
                <option value="processing">Processing</option>
                <option value="failed">Failed</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Board view — short-circuits the rest of the list-mode render. */}
      {viewMode === "board" && (
        <TendersBoardView rfps={rfps} />
      )}

      {/* Table */}
      {viewMode === "list" && (
      <Card className="border-[#E2E8F0]">
        <CardContent className="p-0">
          <Table data-testid="rfp-table">
            <TableHeader>
              <TableRow className="bg-[#F1F5F9]">
                <TableHead 
                  className="cursor-pointer hover:bg-[#E2E8F0]"
                  onClick={() => handleSort("name")}
                >
                  <div className="flex items-center gap-2">
                    RFP Name
                    <ArrowUpDown className="w-4 h-4" />
                  </div>
                </TableHead>
                <TableHead>Status</TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-[#E2E8F0]"
                  onClick={() => handleSort("total_pages")}
                >
                  <div className="flex items-center gap-2">
                    Pages
                    <ArrowUpDown className="w-4 h-4" />
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-[#E2E8F0]"
                  onClick={() => handleSort("file_size")}
                >
                  <div className="flex items-center gap-2">
                    Size
                    <ArrowUpDown className="w-4 h-4" />
                  </div>
                </TableHead>
                <TableHead>Deadlines</TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-[#E2E8F0]"
                  onClick={() => handleSort("created_at")}
                >
                  <div className="flex items-center gap-2">
                    Uploaded
                    <ArrowUpDown className="w-4 h-4" />
                  </div>
                </TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRFPs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12">
                    <FileText className="w-12 h-12 mx-auto text-[#E2E8F0] mb-3" />
                    <p className="text-[#64748B]">
                      {searchTerm || statusFilter !== "all" 
                        ? "No RFPs match your filters" 
                        : "No RFPs uploaded yet"}
                    </p>
                  </TableCell>
                </TableRow>
              ) : (
                filteredRFPs.map((rfp) => {
                  const statusConfig = getStatusConfig(rfp.status);
                  const StatusIcon = statusConfig.icon;
                  return (
                    <TableRow 
                      key={rfp.id} 
                      className="hover:bg-[#F8FAFC] cursor-pointer"
                      data-testid={`rfp-row-${rfp.id}`}
                    >
                      <TableCell onClick={() => navigate(`/tender/${rfp.id}`)}>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-[#F1F5F9] rounded-lg flex items-center justify-center">
                            <FileText className="w-5 h-5 text-[#007AFF]" />
                          </div>
                          <div>
                            <p className="font-medium text-[#0F172A] max-w-[300px] truncate">
                              {rfp.name}
                            </p>
                            <p className="text-xs text-[#64748B]">{rfp.file_type?.toUpperCase()}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          className={statusConfig.color}
                          title={rfp.parse_error || statusConfig.label}
                        >
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {/* When the backend marked the row FAILED because
                              the upload file is missing on disk, show a
                              clearer "file missing — re-upload" label so
                              the BD user knows to re-upload rather than
                              banging on the analyse button. */}
                          {rfp.status === "failed" &&
                           (rfp.parse_error || "").toLowerCase().includes("missing")
                            ? "File missing — re-upload"
                            : statusConfig.label}
                        </Badge>
                      </TableCell>
                      <TableCell>{rfp.total_pages || 0}</TableCell>
                      <TableCell>{formatFileSize(rfp.file_size)}</TableCell>
                      <TableCell>
                        {rfp.key_deadlines?.length > 0 ? (
                          <div className="flex items-center gap-1">
                            <Calendar className="w-4 h-4 text-red-500" />
                            <span className="text-sm">{rfp.key_deadlines.length}</span>
                          </div>
                        ) : (
                          <span className="text-[#94A3B8]">-</span>
                        )}
                      </TableCell>
                      <TableCell>{formatDate(rfp.created_at)}</TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => navigate(`/tender/${rfp.id}`)}
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {rfp.status === "analyzed" && (
                            <>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => navigate(`/chat?rfp=${rfp.id}`)}
                                title="Chat"
                              >
                                <MessageSquare className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => navigate(`/tender/${rfp.id}`)}
                                title="Open tender workspace"
                              >
                                <Wand2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="icon" title="Delete">
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete RFP?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will permanently delete "{rfp.name}" and all its analysis data.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction 
                                  onClick={() => handleDelete(rfp.id, rfp.name)}
                                  className="bg-red-600 hover:bg-red-700"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      )}
    </div>
  );
};

export default RFPList;
